import { useState, useEffect } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Search, Warehouse, Package, MapPin, ChevronLeft, ChevronRight, ImageOff, Boxes } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const ALL = "__ALL__";
const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

function ProductImage({ src, alt, className }) {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 text-slate-300 ${className}`}>
        <ImageOff size={28} />
      </div>
    );
  }
  return <img src={src} alt={alt || ""} loading="lazy" onError={() => setErr(true)}
    className={`object-cover bg-white ${className}`} />;
}

function StockPill({ label, qty, tone }) {
  const tones = {
    bodega: qty > 0 ? "bg-[#EA580C]/10 text-[#9A3412] border-[#FED7AA]" : "bg-slate-100 text-slate-400 border-slate-200",
    total: qty > 0 ? "bg-[#059669]/10 text-[#065F46] border-[#A7F3D0]" : "bg-slate-100 text-slate-400 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[11px] font-medium ${tones[tone]}`}>
      {label} <span className="font-mono font-semibold">{qty}</span>
    </span>
  );
}

function ProductCard({ p, onClick }) {
  return (
    <button data-testid={`inv-card-${p.product_norm}`} onClick={onClick}
      className="group text-left rounded-sm border border-[#E2E8F0] bg-white overflow-hidden hover:border-[#EA580C] hover:shadow-sm transition-all">
      <div className="relative aspect-square w-full overflow-hidden">
        <ProductImage src={p.image_url} alt={p.description} className="h-full w-full group-hover:scale-[1.03] transition-transform duration-300" />
        {p.category && (
          <span className="absolute top-1.5 left-1.5 rounded-sm bg-white/90 backdrop-blur text-[10px] text-slate-600 px-1.5 py-0.5 border border-slate-200">{p.category}</span>
        )}
      </div>
      <div className="p-2.5 space-y-1.5">
        <p className="text-sm font-medium text-slate-800 leading-tight line-clamp-2 min-h-[2.4em]">{p.description || p.product}</p>
        <p className="text-[11px] text-slate-400 font-mono">{p.product} · {p.brand || "—"}</p>
        <div className="flex items-center justify-between pt-0.5">
          <span className="text-sm font-mono font-semibold text-slate-900">{money(p.price)}</span>
        </div>
        <div className="flex flex-wrap gap-1">
          <StockPill label="Total" qty={p.total_available} tone="total" />
          <StockPill label="Bodega" qty={p.bodega_available} tone="bodega" />
        </div>
      </div>
    </button>
  );
}

function ProductDialog({ productNorm, open, onClose }) {
  const { data: p } = useQuery({
    queryKey: ["inventory-product", productNorm],
    queryFn: async () => (await api.get(`/recon/inventory/product/${productNorm}`)).data,
    enabled: !!productNorm && open,
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm" data-testid="inv-product-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{p?.description || p?.product || "Producto"}</DialogTitle>
        </DialogHeader>
        {p && (
          <div className="grid sm:grid-cols-2 gap-5">
            <ProductImage src={p.image_url} alt={p.description} className="w-full aspect-square rounded-sm border border-[#E2E8F0]" />
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-slate-600">
                <span><span className="text-slate-400">Modelo:</span> <span className="font-mono">{p.product}</span></span>
                <span><span className="text-slate-400">Marca:</span> {p.brand || "—"}</span>
                <span><span className="text-slate-400">Categoría:</span> {p.category || "—"}</span>
                <span><span className="text-slate-400">Precio:</span> <span className="font-mono font-semibold text-slate-900">{money(p.price)}</span></span>
              </div>
              <div className="flex gap-2">
                <div className="flex-1 rounded-sm border border-[#A7F3D0] bg-[#ECFDF5] p-2.5 text-center">
                  <div className="font-mono text-xl font-light text-[#065F46]">{p.total_available}</div>
                  <div className="text-[11px] uppercase tracking-wide text-[#065F46]">Disponible total</div>
                </div>
                <div className="flex-1 rounded-sm border border-[#FED7AA] bg-[#FFF7ED] p-2.5 text-center">
                  <div className="font-mono text-xl font-light text-[#9A3412]">{p.bodega_available}</div>
                  <div className="text-[11px] uppercase tracking-wide text-[#9A3412]">En bodega (11)</div>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1.5 flex items-center gap-1.5"><MapPin size={13} /> Stock por ubicación</p>
                <div className="max-h-48 overflow-auto thin-scroll rounded-sm border border-[#E2E8F0]">
                  <table className="w-full text-sm">
                    <tbody>
                      {(p.breakdown || []).map((b) => (
                        <tr key={b.location} className={`border-b last:border-0 border-[#F1F5F9] ${b.is_bodega ? "bg-[#FFF7ED]" : ""}`}>
                          <td className="px-2.5 py-1.5 text-slate-700">{b.name}</td>
                          <td className="px-2.5 py-1.5 text-right font-mono font-semibold text-slate-900">{b.qty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function InventoryPanel() {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [bodegaOnly, setBodegaOnly] = useState(false);
  const [sort, setSort] = useState("stock");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const { data: info } = useQuery({
    queryKey: ["inventory-info"],
    queryFn: async () => (await api.get("/recon/inventory")).data,
  });
  const { data: opts } = useQuery({
    queryKey: ["inventory-filter-options"],
    queryFn: async () => (await api.get("/recon/inventory/filter-options")).data,
  });
  const { data, isFetching } = useQuery({
    queryKey: ["inventory-catalog", debounced, category, location, bodegaOnly, sort, page],
    queryFn: async () => (await api.get("/recon/inventory/catalog", {
      params: { search: debounced || undefined, category: category || undefined, location: location || undefined, bodega_only: bodegaOnly, sort, page, page_size: 40 },
    })).data,
    placeholderData: keepPreviousData,
  });

  const meta = info?.meta;
  const items = data?.items || [];
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / 40));

  return (
    <div className="space-y-4" data-testid="inventory-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Boxes size={20} className="text-[#EA580C]" /> Inventario
          </h1>
          {meta && (
            <p className="text-sm text-slate-500 mt-0.5">
              <span className="font-mono text-slate-700">{meta.products.toLocaleString()}</span> productos ·
              <span className="font-mono text-slate-700"> {meta.total_units.toLocaleString()}</span> unidades ·
              <span className="font-mono text-[#9A3412]"> {meta.bodega_products.toLocaleString()}</span> en bodega
            </p>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="inv-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar producto, modelo, marca…"
            className="h-9 rounded-sm border-slate-300 bg-white text-sm pl-8 w-[260px]" />
        </div>
        <Select value={category || ALL} onValueChange={(v) => { setCategory(v === ALL ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="inv-category" className="h-9 rounded-sm border-slate-300 bg-white text-sm !w-auto min-w-[150px]"><SelectValue placeholder="Categoría" /></SelectTrigger>
          <SelectContent className="rounded-sm max-h-72">
            <SelectItem value={ALL}>Todas las categorías</SelectItem>
            {(opts?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={location || ALL} onValueChange={(v) => { setLocation(v === ALL ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="inv-location" className="h-9 rounded-sm border-slate-300 bg-white text-sm !w-auto min-w-[150px]"><SelectValue placeholder="Ubicación" /></SelectTrigger>
          <SelectContent className="rounded-sm max-h-72">
            <SelectItem value={ALL}>Todas las ubicaciones</SelectItem>
            {(opts?.locations || []).map((l) => <SelectItem key={l.number} value={l.number}>{l.number} · {l.description}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant={bodegaOnly ? "default" : "outline"} size="sm" data-testid="inv-bodega-toggle"
          onClick={() => { setBodegaOnly((v) => !v); setPage(1); }}
          className={`h-9 rounded-sm gap-1.5 ${bodegaOnly ? "bg-[#EA580C] hover:bg-[#EA580C]/90 text-white" : "border-slate-300 text-slate-600"}`}>
          <Warehouse size={14} /> Solo bodega
        </Button>
        <Select value={sort} onValueChange={(v) => setSort(v)}>
          <SelectTrigger data-testid="inv-sort" className="h-9 rounded-sm border-slate-300 bg-white text-sm !w-auto min-w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value="stock">Más disponible</SelectItem>
            <SelectItem value="name">Nombre A-Z</SelectItem>
            <SelectItem value="price">Precio mayor</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-400 ml-auto font-mono">{total.toLocaleString()} resultados</span>
      </div>

      {/* Grid */}
      {items.length === 0 ? (
        <div className="py-20 text-center text-slate-400 flex flex-col items-center gap-2" data-testid="inv-empty">
          <Package size={28} /> {isFetching ? "Cargando…" : "Sin productos para este filtro"}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {items.map((p) => (
            <ProductCard key={p.product_norm} p={p} onClick={() => setSelected(p.product_norm)} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            data-testid="inv-prev" className="h-8 rounded-sm border-slate-300 gap-1"><ChevronLeft size={14} /> Anterior</Button>
          <span className="text-sm text-slate-500 font-mono">{page} / {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
            data-testid="inv-next" className="h-8 rounded-sm border-slate-300 gap-1">Siguiente <ChevronRight size={14} /></Button>
        </div>
      )}

      <ProductDialog productNorm={selected} open={!!selected} onClose={() => setSelected(null)} />
    </div>
  );
}
